import React, { useEffect, useRef, useState } from 'react'
import { Resource, ResourceFile, ResourceFolder } from '../../utils/interfaces'
import { FileIcon, JavaIcon, JsonIcon, PythonIcon, YmlIcon } from '../../utils/svgs'
import { useEventListener } from '@iwbam/react-ez'
import Popup from './Popup'
import SelectFolderButton from './SelectFolderButton'
import { getPathFromId } from '../../utils/functions'

interface Props {
  resource: Resource
  file: ResourceFile
  style?: React.CSSProperties
  fontStyle?: React.CSSProperties
  showButtonAlways?: boolean
  handleMove: (id: string, newDirId: string) => void
  handleDelete: (id: string) => void
  handleRename: (id: string, newName: string) => void
  openFile: (file: ResourceFile) => void
}

const ResourceFileButton: React.FC<Props> = (props) => {

  const optionButtonRef = useRef(null);
  const [isOptionOpening, setIsOptionOpening] = useState(false);
  const [optionPos, setOptionPos] = useState({ top: -1000, left: -1000 });
  // rename & delete & move
  const [renameInput, setRenameInput] = useState('');
  const [isFileRenaming, setIsFileRenaming] = useState(false);
  const [isFileMoving, setIsFileMoving] = useState(false);
  const [newDirId, setNewDirId] = useState('');
  const [isFileDeleting, setIsFileDeleting] = useState(false);

  // set position of options
  useEffect(() => {
    if (isOptionOpening && optionButtonRef.current) {
      const btn = optionButtonRef.current as HTMLElement;
      const rect = btn.getBoundingClientRect();
      setOptionPos({ top: rect.top, left: rect.left + rect.width });
    }
  }, [isOptionOpening]);

  // handle click
  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest(`#z${props.file.id.replace('.', '')}-options`) && !target.closest(`#z${props.file.id.replace('.', '')}-options-btn`)) {
      setIsOptionOpening(false);
    }
  });

  // handle key down
  useEventListener('keydown', (e) => {
    if (e.key === 'Enter' && isFileRenaming) rename();
  });

  // move file
  const moveFile = () => {
    if (!isFileMoving) return;
    props.handleMove(props.file.id, newDirId);
    setIsFileMoving(false);
    setNewDirId('');
  }

  // delete file
  const deleteFile = () => {
    if (!isFileDeleting) return;
    props.handleDelete(props.file.id);
    setIsFileDeleting(false);
  }
  
  // rename
  const rename = () => {
    if (!isFileRenaming) return;
    props.handleRename(props.file.id, renameInput);
    setIsFileRenaming(false);
    setRenameInput('');
  }

  // select file
  const handleSelect = () => props.openFile(props.file);

  return (
    <>
      <div className='h-5 px-1 rounded flex items-center justify-between cursor-pointer *:hover:flex hover:bg-gray-800 m-0.5' key={props.file.id}>
        {/* name */}
        <div className='flex items-center flex-1 overflow-hidden' onClick={handleSelect}>
          <div className='p-1 w-5 flex items-center justify-center h-5'>{extensionToIcon[props.file.name.split('.').pop() || ''] || <FileIcon />}</div>
          <div className={`text-xs flex-1 overflow-hidden text-ellipsis text-nowrap`} style={props.fontStyle}>{props.file.name}</div>
        </div>
        {/* buttons */}
        <div className={`items-center hidden ml-2`}>
          <button ref={optionButtonRef} onClick={() => setIsOptionOpening(true)} id={`z${props.file.id.replace('.', '')}-options-btn`} className='p-0.5 flex items-center justify-center h-4 aspect-square rounded-sm hover:bg-gray-800 ml-1.5'><i className='fa-solid fa-ellipsis text-xs' /></button>
        </div>
      </div>
      {isOptionOpening && <div id={`z${props.file.id.replace('.', '')}-options`} className='fixed border overflow-hidden flex flex-col rounded bg-neutral-900 z-10 w-24' style={{ top: optionPos.top, left: optionPos.left }}>
        <button onClick={() => {
          setIsFileRenaming(true);
          setRenameInput(props.file.name);
          setIsOptionOpening(false);
        }} className='text-xs p-1 px-2 text-left hover:bg-gray-800'>Rename</button>
        <button onClick={() => {
          setIsFileMoving(true);
          setNewDirId(props.resource.root.id);
          setIsOptionOpening(false);
        }} className='text-xs p-1 px-2 text-left hover:bg-gray-800'>Move</button>
        <button onClick={() => {
          setIsFileDeleting(true);
          setIsOptionOpening(false);
        }} className='text-xs p-1 px-2 text-left hover:bg-gray-800'>Delete</button>
      </div>}

      {/* popup */}

      {isFileMoving && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md move-popup'>
          <div className='font-semibold'>Move file "<span className='font-bold'>{props.file.name}</span>"</div>
          <input className='border rounded cursor-default mt-4 py-1 px-2 text-sm w-full' value={getPathFromId(props.resource.root, newDirId)} readOnly />
          <div className='border rounded text-sm w-full mt-4 h-56 scroller overflow-hidden'>
            <SelectFolderButton 
              folder={props.resource.root}
              selectedId={newDirId}
              setSelectedId={setNewDirId}
            />
          </div>
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={moveFile} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600'>Move</button>
            <button onClick={() => setIsFileMoving(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isFileDeleting && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md delete-popup'>
          <div className='font-bold'>Delete file</div>
          <div className='mt-4 text-sm font-light'>Delete file <span className='font-bold'>{props.file.name}</span>?</div>
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={deleteFile} className='font-bold bg-red-600 p-1 px-4 rounded active:bg-red-700'>Delete</button>
            <button onClick={() => setIsFileDeleting(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}

      {isFileRenaming && <div className='fixed z-50 top-0 left-0 w-screen h-svh flex items-center justify-center'>
        <Popup className='max-w-md rename-popup'>
          <div className='font-bold'>Rename file</div>
          <input className='border rounded mt-4 py-1 px-2 text-sm w-full' value={renameInput} onInput={(e: React.ChangeEvent<HTMLInputElement>) => setRenameInput(e.target.value)} />
          <div className='flex justify-end text-sm mt-8'>
            <button onClick={rename} className='font-bold bg-emerald-500 p-1 px-4 rounded active:bg-emerald-600'>Rename</button>
            <button onClick={() => setIsFileRenaming(false)} className='font-bold bg-gray-600 p-1 px-4 rounded ml-4 active:bg-gray-700'>Cancel</button>
          </div>
        </Popup>
      </div>}
    </>
  )
}

export const extensionToIcon: { [key: string]: JSX.Element } = {
  'java': <JavaIcon />,
  'yaml': <YmlIcon />,
  'yml': <YmlIcon />,
  'json': <JsonIcon />,
  'py': <PythonIcon />,
  'jar': <JavaIcon singleColor />,
};


export default ResourceFileButton